"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback
} from "react";
import cx from "classnames";
import { ListBoxContext } from "./ListBoxContext.mjs";
import handleKeyDown, { getElementsArray } from "./utils/handleKeyDown.mjs";
import { getHorisontalPosition } from "./utils/position.mjs";
import { throttle } from "./utils/throttle.mjs";
import { useWindowSize } from "@ingka/viewport-hooks";
const ListBoxInternal = (props) => {
  const {
    prefix = "",
    children,
    className,
    open = false,
    footer,
    multiple = true,
    onChange,
    domFocus = true,
    ariaLabel = "Listbox",
    trigger,
    position = "left",
    ...otherProps
  } = props;
  const optionsContainerRef = useRef(null);
  const prevOpenRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const { selectedItems } = useContext(ListBoxContext);
  const [isClosed, setIsClosed] = useState(!open);
  const [horisontalPosition, setHorisontalPosition] = useState(position);
  const [verticalPosition, setVerticalPosition] = useState(
    "bottom"
  );
  const triggerContainerRef = useRef(null);
  const innerRef = useRef(null);
  const windowSize = useWindowSize(open);
  const withTriggerMargin = 16;
  const baseClass = `${prefix}list-box`;
  const classes = cx(
    baseClass,
    `${baseClass}--${horisontalPosition}`,
    `${baseClass}--${verticalPosition}`,
    isOpen && `${baseClass}--open`,
    isClosed && `${baseClass}--closed`,
    className
  );
  useEffect(() => {
    if (onChange && selectedItems) {
      onChange(selectedItems);
    }
  }, [selectedItems]);
  useEffect(() => {
    if (isOpen && optionsContainerRef.current && domFocus && prevOpenRef.current !== isOpen) {
      const elements = getElementsArray(optionsContainerRef.current, prefix);
      const firstSelectedIndex = elements.findIndex(
        (element) => selectedItems?.includes(element.id)
      );
      if (firstSelectedIndex !== -1) {
        elements[firstSelectedIndex].focus();
      } else {
        elements[0]?.focus();
      }
    }
  }, [getElementsArray, isOpen, domFocus, selectedItems]);
  const handleTransitionEnd = (ev) => {
    if (isOpen && ev.propertyName !== "opacity" || ev.target !== innerRef.current)
      return;
    setIsClosed(true);
  };
  const handlePositionChange = useCallback(() => {
    if (isOpen && innerRef.current && windowSize) {
      const element = innerRef.current.getBoundingClientRect();
      const triggerRect = triggerContainerRef.current?.getBoundingClientRect();
      if (!triggerRect?.height)
        return;
      const triggerHeight = triggerRect.height;
      const bottomPosition = windowSize.h - element.bottom;
      const topPosition = element.top;
      if (bottomPosition <= 0 && element.height + triggerHeight + withTriggerMargin < topPosition) {
        setVerticalPosition("top");
      } else if (topPosition < 0 || bottomPosition - triggerHeight - element.height - withTriggerMargin > 0) {
        setVerticalPosition("bottom");
      }
      const calculatedPosition = getHorisontalPosition({
        preferredPosition: position,
        containerWidth: document.documentElement.clientWidth,
        triggerLeft: triggerRect.left,
        triggerRight: triggerRect.right,
        menuWidth: element.width
      });
      setHorisontalPosition(calculatedPosition);
    }
  }, [isOpen, position, windowSize]);
  const throttledHandlePositionChange = throttle(handlePositionChange, 200);
  useEffect(() => {
    document.addEventListener("scroll", throttledHandlePositionChange);
    return () => {
      document.removeEventListener("scroll", throttledHandlePositionChange);
    };
  }, [throttledHandlePositionChange]);
  useEffect(() => {
    setHorisontalPosition(position);
  }, [position]);
  useEffect(() => {
    prevOpenRef.current = isOpen;
    handlePositionChange();
  }, [isOpen]);
  useEffect(() => {
    setIsOpen(open);
    if (open)
      setIsClosed(false);
  }, [open]);
  useEffect(handlePositionChange, [handlePositionChange, windowSize]);
  const listBoxContent = /* @__PURE__ */ jsxs(
    "div",
    {
      className: classes,
      onTransitionEnd: handleTransitionEnd,
      ref: innerRef,
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: `${baseClass}__body`,
            ref: optionsContainerRef,
            tabIndex: domFocus ? 0 : void 0,
            onKeyDown: (e) => handleKeyDown(e, prefix),
            role: "listbox",
            "aria-label": ariaLabel,
            "aria-multiselectable": multiple,
            ...otherProps,
            children
          }
        ),
        footer
      ]
    }
  );
  return /* @__PURE__ */ jsx(Fragment, { children: trigger ? /* @__PURE__ */ jsxs("div", { className: `${prefix}list-box__trigger`, ref: triggerContainerRef, children: [
    trigger,
    listBoxContent
  ] }) : listBoxContent });
};
ListBoxInternal.displayName = "ListBoxInternal";
var ListBoxInternal_default = ListBoxInternal;
export {
  ListBoxInternal_default as default
};
